import React, { useEffect, useMemo, useState } from 'react';
import isEmpty from 'lodash/isEmpty';
import styled, { css } from 'styled-components';
import {
  Box,
  Button,
  Text,
  useMediaQuery,
} from '@sparknz/spark-pattern-library';
import { LinkRegion } from '@sparknz/tracking';

import { DeviceGalleryQuery } from '__generated__/DeviceGalleryQuery.graphql';
import {
  CARD_LOAD_SIZE_DESKTOP,
  CARD_LOAD_SIZE_NON_DESKTOP,
} from 'shared/components/ProductGalleryCard/constants/deviceGalleryCard.constant';
import { GalleryProductsWithPosition } from 'shared/components/ProductGalleryCard/typings';
import { getGalleryProductsWithPosition } from 'shared/components/ProductGalleryCard/utils/getDeviceGalleryCardContent.utils';
import { useSegment } from 'shared/hooks';
import DeviceGalleryCard from '../DeviceGalleryCard';

interface CardContainerProps {
  filteredProducts: DeviceGalleryQuery['response']['deviceGallery']['products'];
  selectedSubcategoryId: string;
}

export const ItemGridContainer = styled.div`
  ${({ theme: { media, spacing } }) => css`
    display: grid;
    width: 100%;
    justify-items: stretch;
    grid-template-columns: 1fr;
    grid-auto-rows: auto;
    gap: ${spacing.s3};
    padding: ${spacing.s2} 0 ${spacing.s6};

    ${media.up('slim')} {
      grid-template-columns: repeat(2, 1fr);
    }

    ${media.up('big')} {
      grid-template-columns: repeat(3, 1fr);
    }
  `}
`;

const CenteredText = styled(Text)`
  text-align: center;
`;

const LoadMoreButton = styled(Button)`
  ${({ theme: { media } }) => css`
    width: 100%;

    ${media.up('slim')} {
      width: 144px;
    }
  `}
`;

const CardsContainer = ({
  filteredProducts,
  selectedSubcategoryId,
}: CardContainerProps) => {
  const { isBusiness } = useSegment();

  const isDesktop = useMediaQuery({ min: 'big' });
  const isTablet = useMediaQuery({ min: 'slim', max: 'big' });

  const loadSize = isDesktop
    ? CARD_LOAD_SIZE_DESKTOP
    : CARD_LOAD_SIZE_NON_DESKTOP;

  const galleryProductsWithPosition = useMemo(() => {
    const tabletCardCount = isTablet ? 2 : 1;
    const cardsCountInSameRow = isDesktop ? 3 : tabletCardCount;
    return getGalleryProductsWithPosition(
      filteredProducts,
      cardsCountInSameRow,
      isBusiness,
    );
  }, [filteredProducts, isBusiness, isDesktop, isTablet]);

  const initialCardList = useMemo(
    () => galleryProductsWithPosition.slice(0, loadSize),
    [galleryProductsWithPosition],
  );

  const [productCardList, setProductCardList] =
    useState<GalleryProductsWithPosition>(initialCardList);

  useEffect(() => {
    setProductCardList(initialCardList);
  }, [initialCardList]);

  const handleLoadMore = () => {
    setProductCardList(
      galleryProductsWithPosition.slice(0, productCardList.length + loadSize),
    );
  };

  if (isEmpty(filteredProducts)) {
    return (
      <Box justifyContent="center" py="s8">
        <CenteredText variant="bodyMMedium">
          There are no products available for this category
        </CenteredText>
      </Box>
    );
  }

  return (
    <>
      <ItemGridContainer data-testid="ProductCardsContainer">
        {productCardList?.map((product, index) => (
          <DeviceGalleryCard
            isBusiness={isBusiness}
            key={product.offerId}
            numProducts={filteredProducts.length}
            product={product}
            selectedSubcategoryId={selectedSubcategoryId}
            eagerLoadImage={index < 3} // Eager load the first 3 images in the gallery
          />
        ))}
      </ItemGridContainer>
      <CenteredText variant="bodySMedium" mb="s2">
        Showing {productCardList.length} of {filteredProducts.length} items
      </CenteredText>
      <LinkRegion regionId="gallery">
        <Box
          justifyContent="center"
          alignItems="center"
          flexDirection="row"
          pb="s4"
        >
          {productCardList?.length === filteredProducts.length ? null : (
            <LoadMoreButton onClick={handleLoadMore} variant="default">
              Load more
            </LoadMoreButton>
          )}
        </Box>
      </LinkRegion>
    </>
  );
};

const MemoizedCardContainer = React.memo(CardsContainer);
export default MemoizedCardContainer;
